import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@/components/primitives";
import { MobileFrame } from "@/components/MobileFrame";
import {
  disposeLocalPhoto,
  expandPickedFiles,
  makeLocalPhoto,
  uploadPhotos,
  MAX_PHOTOS,
  type LocalPhoto,
} from "@/lib/photos";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { parseMarketplaceLinks, urlPrefixFor } from "@/lib/marketplaces";
import { notify } from "@/lib/nativeHaptics";
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// A photo slot in the unified sortable grid — either a hosted URL (from a
// prior scan) or a locally-picked file (to be uploaded on submit).
type PhotoSlot =
  | { kind: "remote"; id: string; url: string }
  | { kind: "local"; id: string; local: LocalPhoto };

function slotPreview(s: PhotoSlot): string {
  return s.kind === "remote" ? s.url : s.local.previewUrl;
}

function SortablePhotoSlot({
  slot, index, onRemove,
}: { slot: PhotoSlot; index: number; onRemove: () => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: slot.id });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.45 : 1,
        zIndex: isDragging ? 999 : 1,
        position: "relative",
        aspectRatio: "1",
        overflow: "hidden",
        border: index === 0 ? "2px solid hsl(var(--rust))" : "1px solid hsl(var(--ink))",
        background: "hsl(var(--ink))",
        touchAction: "none",
        cursor: isDragging ? "grabbing" : "grab",
      }}
      {...attributes}
      {...listeners}
    >
      <img
        src={slotPreview(slot)}
        alt={`Photo ${index + 1}`}
        draggable={false}
        style={{ width: "100%", height: "100%", objectFit: "cover", pointerEvents: "none" }}
      />
      {index === 0 && (
        <div style={{
          position: "absolute", top: 6, left: 6,
          background: "hsl(var(--rust))", color: "hsl(var(--rust-fg))",
          padding: "3px 6px", fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase",
          pointerEvents: "none",
        }}>Cover</div>
      )}
      {slot.kind === "remote" && index > 0 && (
        <div style={{
          position: "absolute", top: 6, left: 6,
          background: "hsl(var(--ink))", color: "hsl(var(--cream))",
          padding: "3px 6px", fontFamily: "'IBM Plex Mono', monospace",
          fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase",
          pointerEvents: "none",
        }}>From Fig</div>
      )}
      <button
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        aria-label="Remove"
        style={{
          position: "absolute", top: 6, right: 6,
          width: 26, height: 26, border: "none",
          background: "rgba(0,0,0,0.7)", color: "hsl(var(--cream))",
          cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 0,
          zIndex: 10,
        }}
      >×</button>
    </div>
  );
}

type Step = "photos" | "details" | "where" | "review";

// Each marketplace gets a URL prefix; the user only fills in their username
// and we glue the full link together at submit time. `prefix: null` means
// no autogeneration (the user pastes a full URL or no URL at all).
// Each marketplace gets a URL prefix and a strict handle pattern. The user
// only fills in their username — we glue the full link together at submit
// time. `prefix: null` means no autogeneration.
//
// Patterns intentionally only allow the characters each platform documents
// for handles (letters, digits, and a small set of separators) plus a sane
// length cap. This blocks injection attempts (`/`, `?`, `#`, whitespace,
// quotes, angle brackets, etc.) before we ever build a URL.
const PLATFORMS: {
  name: string;
  prefix: string | null;
  pattern?: RegExp;
  hint?: string;
}[] = [
  { name: "Depop",     prefix: "https://www.depop.com/",       pattern: /^[a-zA-Z0-9_.]{2,30}$/, hint: "letters, numbers, _ or . — up to 30 chars" },
  { name: "Poshmark",  prefix: "https://poshmark.com/closet/", pattern: /^[a-zA-Z0-9_]{2,30}$/,  hint: "letters, numbers, or _ — up to 30 chars" },
  { name: "Etsy",      prefix: "https://www.etsy.com/shop/",   pattern: /^[a-zA-Z0-9]{4,20}$/,   hint: "letters and numbers only — 4 to 20 chars" },
  { name: "eBay",      prefix: "https://www.ebay.com/usr/",    pattern: /^[a-zA-Z0-9_.-]{6,64}$/, hint: "letters, numbers, _ . or - — 6 to 64 chars" },
  { name: "Vinted",    prefix: "https://www.vinted.com/member/", pattern: /^[a-zA-Z0-9_.-]{2,30}$/, hint: "letters, numbers, _ . or - — up to 30 chars" },
  { name: "Grailed",   prefix: "https://www.grailed.com/",     pattern: /^[a-zA-Z0-9_.-]{2,30}$/, hint: "letters, numbers, _ . or - — up to 30 chars" },
  { name: "Personal Site", prefix: null },
  { name: "In person",     prefix: null },
  { name: "Other",         prefix: null },
];

const platformByName = (name: string) => PLATFORMS.find((p) => p.name === name);

/** Returns an error message if the handle is invalid, or null if OK / empty. */
function validateHandle(platform: string, raw: string): string | null {
  const def = platformByName(platform);
  if (!def?.pattern) return null;
  const trimmed = raw.trim().replace(/^@/, "");
  if (!trimmed) return null; // empty is allowed (URL stays optional)
  if (!def.pattern.test(trimmed)) {
    return `Invalid ${def.name} username — ${def.hint ?? "use letters and numbers only"}.`;
  }
  return null;
}

const STEP_ORDER: Step[] = ["photos", "details", "where", "review"];

const STEP_LABEL: Record<Step, string> = {
  photos: "Photos",
  details: "Details",
  where: "Where",
  review: "Review",
};

export default function QuickPost() {
  const nav = useNavigate();
  const location = useLocation();
  const fromScan = (location.state as { fromScan?: {
    scanId?: string | null;
    photos?: string[];
    title?: string;
    brand?: string;
    size?: string;
    condition?: string;
    conditionNotes?: string;
    era?: string;
    category?: string;
    material?: string;
    color?: string;
    notes?: string[];
    tags?: string[];
    verification?: { tier?: string } | null;
    verdict?: string;
    verdictLabel?: string;
    confidence?: number;
    priceSuggested?: number;
  } } | null)?.fromScan ?? null;
  const { user } = useAuth();

  // A scan that came in from Fig with a PASS verdict carries the verified
  // vintage stamp into the post. We surface this in the review preview and
  // tweak the footer copy so the collector knows the badge will travel with
  // the post.
  const isVerifiedFromScan = !!fromScan && fromScan.verdict === "PASS";

  // ── flow state ───────────────────────────────────────────────
  const [step, setStep] = useState<Step>(fromScan ? "review" : "photos");

  // photos — unified sortable list of both remote (from scan) and local picks
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const [slots, setSlots] = useState<PhotoSlot[]>(() =>
    (fromScan?.photos ?? []).map((url) => ({ kind: "remote" as const, id: url, url })),
  );
  const [picking, setPicking] = useState(false);

  // details
  const knownConditions = ["Excellent", "Very good", "Good", "Fair", "Distressed"];
  const [title, setTitle] = useState(fromScan?.title ?? "");
  const [brand, setBrand] = useState(fromScan?.brand ?? "");
  const [size, setSize] = useState(fromScan?.size ?? "");
  const [condition, setCondition] = useState(fromScan?.condition ?? "");
  const [conditionChoice, setConditionChoice] = useState(
    fromScan?.condition
      ? (knownConditions.includes(fromScan.condition) ? fromScan.condition : "Other")
      : "",
  );
  const [conditionOther, setConditionOther] = useState(
    fromScan?.condition && !knownConditions.includes(fromScan.condition)
      ? fromScan.condition
      : "",
  );
  const [deadstock, setDeadstock] = useState(false);
  const [caption, setCaption] = useState(
    fromScan?.notes?.length ? fromScan.notes.slice(0, 2).join(" ").slice(0, 600) : "",
  );

  // where
  const [whereKind, setWhereKind] = useState<"selling" | "kept">("selling");
  // Multi-select: a user can list the same item on Depop AND Poshmark, or
  // sell it in person while it's also on Etsy. Each selected platform gets
  // its own handle / URL / business-name state, all keyed by platform name.
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [usernames, setUsernames] = useState<Record<string, string>>({});
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [city, setCity] = useState("");

  const togglePlatform = (name: string) => {
    setSelectedPlatforms((prev) =>
      prev.includes(name) ? prev.filter((p) => p !== name) : [...prev, name],
    );
  };

  // review/submit
  const [posting, setPosting] = useState(false);

  // Pull a default city + the user's saved shops from their profile so we
  // can prefill platform usernames at post time. Anything they typed on
  // this page wins (we never overwrite a non-empty field), so prefilled
  // values stay fully editable.
  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("city, marketplace_profiles")
        .eq("id", user.id)
        .maybeSingle();
      if (data?.city) setCity((c) => c || data.city!);

      const saved = parseMarketplaceLinks(
        (data as unknown as { marketplace_profiles?: unknown } | null)?.marketplace_profiles,
      );
      if (saved.length === 0) return;

      // For each saved shop, derive a username from its URL by stripping
      // the canonical prefix. If we can't (custom domain, no prefix on
      // file), fall back to filling the freeform URL field instead.
      const nextUsernames: Record<string, string> = {};
      const nextUrls: Record<string, string> = {};
      const haveShop: string[] = [];
      for (const link of saved) {
        haveShop.push(link.name);
        if (link.name === "In person" || link.name === "Personal Site") {
          // No canonical prefix — In Person stores an address/maps URL and
          // Personal Site stores the seller's site URL. Prefill the freeform
          // urls map directly with whatever's on file.
          if (link.url) nextUrls[link.name] = link.url;
          continue;
        }
        const prefix = urlPrefixFor(link.name);
        if (prefix && link.url.toLowerCase().startsWith(prefix.toLowerCase())) {
          const handle = link.url.slice(prefix.length).replace(/[/?#].*$/, "");
          if (handle) nextUsernames[link.name] = handle;
        } else if (link.url) {
          nextUrls[link.name] = link.url;
        }
      }
      // Don't overwrite anything the user has already touched on this page.
      setUsernames((prev) => ({ ...nextUsernames, ...prev }));
      setUrls((prev) => ({ ...nextUrls, ...prev }));
      // Auto-select every saved shop. Users with no shops on file start
      // with nothing selected and can add platforms manually.
      setSelectedPlatforms((prev) =>
        Array.from(new Set([...haveShop, ...prev])),
      );
    })();
  }, [user]);

  // Cleanup local previews on unmount.
  useEffect(() => () => {
    slots.forEach((s) => { if (s.kind === "local") disposeLocalPhoto(s.local); });
  }, []); // eslint-disable-line

  // ── photos ───────────────────────────────────────────────────
  const handleFiles = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const room = MAX_PHOTOS - slots.length;
    if (room <= 0) {
      toast({ title: `Up to ${MAX_PHOTOS} photos`, description: "Remove one before adding another." });
      return;
    }
    setPicking(true);
    try {
      const expanded = await expandPickedFiles(Array.from(fileList));
      const incoming = expanded.slice(0, room);
      const made = await Promise.all(incoming.map(makeLocalPhoto));
      setSlots((prev) => [...prev, ...made.map((local) => ({ kind: "local" as const, id: local.id, local }))]);
    } catch (e) {
      console.error(e);
      toast({ title: "Couldn't read that file", variant: "destructive" });
    } finally {
      setPicking(false);
    }
  };

  const removeSlot = (id: string) => {
    setSlots((prev) => {
      const t = prev.find((s) => s.id === id);
      if (t?.kind === "local") disposeLocalPhoto(t.local);
      return prev.filter((s) => s.id !== id);
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setSlots((prev) => {
        const oldIndex = prev.findIndex((s) => s.id === active.id);
        const newIndex = prev.findIndex((s) => s.id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const photoSensors = useSensors(
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
  );

  // ── navigation ───────────────────────────────────────────────
  const idx = STEP_ORDER.indexOf(step);
  // Live-validate the marketplace handle. Returns null when empty (allowed)
  // or when the value matches the platform's allowlist pattern.
  // Validate every selected platform's handle. The first invalid one (in
  // selection order) is what we surface inline and use to gate Continue.
  const handleErrors = useMemo(() => {
    const out: Record<string, string> = {};
    selectedPlatforms.forEach((name) => {
      const err = validateHandle(name, usernames[name] ?? "");
      if (err) out[name] = err;
    });
    return out;
  }, [selectedPlatforms, usernames]);
  const hasHandleError = Object.keys(handleErrors).length > 0;

  const canAdvance = useMemo(() => {
    if (step === "photos") return slots.length > 0;
    if (step === "details") return title.trim().length > 0;
    if (step === "where") {
      if (whereKind === "kept") return true;
      return selectedPlatforms.length > 0 && !hasHandleError;
    }
    return true;
  }, [step, slots.length, title, whereKind, selectedPlatforms, hasHandleError]);

  const goNext = () => {
    if (!canAdvance) return;
    if (step === "review") return submit();
    setStep(STEP_ORDER[idx + 1]);
  };
  const goBack = () => {
    if (idx === 0) return nav(-1);
    setStep(STEP_ORDER[idx - 1]);
  };

  // ── submit ───────────────────────────────────────────────────
  const submit = async () => {
    if (!user || posting) return;
    setPosting(true);
    try {
      const sessionId = `quick-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      // Preserve user's chosen order: remote slots pass through, local slots get uploaded.
      const localSlots = slots.filter((s): s is Extract<PhotoSlot, { kind: "local" }> => s.kind === "local");
      const uploadedUrls = localSlots.length
        ? await uploadPhotos(user.id, sessionId, localSlots.map((s) => s.local))
        : [];
      const uploadMap = new Map(localSlots.map((s, i) => [s.id, uploadedUrls[i] ?? ""]));
      const photoUrls = slots.map((s) => s.kind === "remote" ? s.url : (uploadMap.get(s.id) ?? "")).filter(Boolean);
      // Build one marketplace_link per selected platform. For platforms
      // with a prefix we glue the validated handle on; for Personal Site,
      // In person, and Other we store the freeform URL/address directly
      // in `url`. We DO NOT mangle the platform name with a business-name
      // suffix anymore — addresses go in the url field where they belong.
      const links =
        whereKind === "selling"
          ? selectedPlatforms.map((name) => {
              const def = platformByName(name);
              let url = "";
              if (def?.prefix) {
                const raw = (usernames[name] ?? "").trim().replace(/^@/, "");
                if (raw) {
                  if (def.pattern && !def.pattern.test(raw)) {
                    throw new Error(`That ${def.name} username has invalid characters.`);
                  }
                  url = `${def.prefix}${encodeURIComponent(raw)}`;
                }
              } else {
                url = (urls[name] ?? "").trim();
              }
              return { name, url };
            })
          : [];

      const insert = {
        user_id: user.id,
        title: title.trim() || "Untitled",
        brand: brand.trim() || null,
        size: size.trim() || null,
        condition: (() => {
          const base = condition.trim();
          if (deadstock && base) return `Deadstock · ${base}`;
          if (deadstock) return "Deadstock";
          return base || null;
        })(),
        caption: caption.trim() || null,
        photos: photoUrls,
        img: "olive",
        // Default to "FAIL" when this is a fresh quick-post (no upstream
        // verification result). "FAIL" is the PASS/FAIL gate that drives
        // the FIG Verified rubber stamp + badge. The post itself surfaces
        // as a regular listing with an "Unverified" treatment — see
        // Post.tsx for the visual. Previously this defaulted to PASS,
        // which gave quick-posts a fake verified badge they didn't earn.
        verdict: (fromScan?.verdict as string) ?? "FAIL",
        confidence: isVerifiedFromScan ? (fromScan?.confidence ?? null) : null,
        verification: isVerifiedFromScan ? (fromScan?.verification as any ?? null) : null,
        posted: true,
        // Posting to feed is always "listed" — "kept" only applies to the
        // Add to Closet action (non-posted). Even without a shop link,
        // sharing publicly is not the same as adding to a personal archive.
        status: "listed",
        post_kind: "manual",
        marketplace_links: links,
        city: city.trim() || null,
      };

      const existingId = fromScan?.scanId ?? null;
      if (existingId) {
        // Update the verification scan in place — never create a duplicate row.
        const { review_status: _ignored, ...updateFields } = { ...insert, review_status: "approved_feed" } as Record<string, unknown>;
        const { error } = await supabase
          .from("scans")
          .update({ ...updateFields, review_status: "approved_feed" } as never)
          .eq("id", existingId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("scans")
          .insert({ ...insert, review_status: "approved_feed" } as never);
        if (error) throw error;
      }

      toast({ title: "Posted", description: "Live in the feed now." });
      void notify(true);
      nav("/feed");
    } catch (e) {
      console.error(e);
      void notify(false);
      const msg = e instanceof Error ? e.message : "Something went wrong";
      toast({ title: "Couldn't post", description: msg, variant: "destructive" });
    } finally {
      setPosting(false);
    }
  };

  // ── styles ───────────────────────────────────────────────────
  const sectionTitle: React.CSSProperties = {
    fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 22,
    color: "hsl(var(--ink))", marginBottom: 4,
  };
  const inputStyle: React.CSSProperties = {
    width: "100%", boxSizing: "border-box", padding: "10px 12px",
    border: "1px solid hsl(var(--ink))", background: "hsl(var(--paper))",
    color: "hsl(var(--ink))", fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 14,
    outline: "none",
  };
  const labelStyle: React.CSSProperties = {
    display: "block", fontFamily: "'IBM Plex Mono', monospace",
    fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase",
    color: "hsl(var(--rust))", marginBottom: 6,
  };

  return (
    <MobileFrame>
      <div style={{ minHeight: "100vh", background: "hsl(var(--paper))", paddingBottom: 140 }}>
        {/* Hidden file inputs */}
        <input ref={cameraRef} type="file" accept="image/*" capture="environment"
          style={{ display: "none" }}
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />
        <input ref={galleryRef} type="file" accept="image/*,.heic,.heif" multiple
          style={{ display: "none" }}
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} />

        {/* Top bar */}
        <div style={{ padding: "12px 20px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button onClick={goBack} aria-label="Back" style={{
            width: 40, height: 40, border: "1px solid hsl(var(--ink))",
            background: "hsl(var(--paper))", color: "hsl(var(--ink))", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {Icon.chev(16, "l")}
          </button>
          <div style={{ textAlign: "center", color: "hsl(var(--ink))" }}>
            <div className="caps" style={{ color: "hsl(var(--rust))" }}>Quick post</div>
            <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 17 }}>
              {STEP_LABEL[step]}
            </div>
          </div>
          <div style={{ width: 40, height: 40 }} />
        </div>

        {/* Step indicator */}
        <div style={{ display: "flex", gap: 6, padding: "16px 20px 0" }}>
          {STEP_ORDER.map((s, i) => (
            <div key={s} style={{
              flex: 1, height: 3,
              background: i <= idx ? "hsl(var(--rust))" : "hsl(var(--ink-2) / 0.25)",
            }} />
          ))}
        </div>

        <div style={{ padding: "22px 20px 0" }}>
          {/* ─── PHOTOS ─── */}
          {step === "photos" && (
            <div>
              <h2 style={sectionTitle}>Add your photos</h2>
              <p style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 13, color: "hsl(var(--ink-2))", marginBottom: 16 }}>
                Up to {MAX_PHOTOS}. The first photo is the cover. Drag handles let you reorder.
              </p>

              {slots.length > 0 && (
                <DndContext sensors={photoSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <SortableContext items={slots.map((s) => s.id)} strategy={rectSortingStrategy}>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10, marginBottom: 16 }}>
                      {slots.map((slot, i) => (
                        <SortablePhotoSlot key={slot.id} slot={slot} index={i} onRemove={() => removeSlot(slot.id)} />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}

              {slots.length < MAX_PHOTOS && (
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={() => galleryRef.current?.click()} disabled={picking} style={{
                    flex: 1, padding: "14px", background: "hsl(var(--ink))",
                    color: "hsl(var(--cream))", border: "1px solid hsl(var(--ink))",
                    cursor: picking ? "wait" : "pointer", display: "flex", alignItems: "center",
                    justifyContent: "center", gap: 8,
                    fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 14,
                  }}>
                    {Icon.plus(16)} Choose from library
                  </button>
                  <button onClick={() => cameraRef.current?.click()} disabled={picking} aria-label="Camera" style={{
                    width: 52, padding: "14px",
                    background: "hsl(var(--paper))", color: "hsl(var(--ink))",
                    border: "1px solid hsl(var(--ink))", cursor: picking ? "wait" : "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>{Icon.camera(18)}</button>
                </div>
              )}
            </div>
          )}

          {/* ─── DETAILS ─── */}
          {step === "details" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <h2 style={sectionTitle}>Tell us about it</h2>
              <div>
                <label style={labelStyle}>Title *</label>
                <input value={title} onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. 70s wool plaid Pendleton shirt" style={inputStyle} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={labelStyle}>Brand</label>
                  <input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Optional" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Size</label>
                  <input value={size} onChange={(e) => setSize(e.target.value)} placeholder="Optional" style={inputStyle} />
                </div>
              </div>
              <div>
                <label style={labelStyle}>Condition</label>
                <select
                  value={conditionChoice}
                  onChange={(e) => {
                    const v = e.target.value;
                    setConditionChoice(v);
                    setCondition(v === "Other" ? conditionOther : v);
                  }}
                  style={{
                    ...inputStyle,
                    appearance: "none",
                    backgroundImage: "linear-gradient(45deg, transparent 50%, hsl(var(--ink)) 50%), linear-gradient(135deg, hsl(var(--ink)) 50%, transparent 50%)",
                    backgroundPosition: "calc(100% - 18px) 50%, calc(100% - 13px) 50%",
                    backgroundSize: "5px 5px, 5px 5px",
                    backgroundRepeat: "no-repeat",
                    paddingRight: 32,
                    cursor: "pointer",
                  }}
                >
                  <option value="">Select condition…</option>
                  <option value="Excellent">Excellent</option>
                  <option value="Very good">Very good</option>
                  <option value="Good">Good</option>
                  <option value="Fair">Fair</option>
                  <option value="Distressed">Distressed</option>
                  <option value="Other">Other…</option>
                </select>
                {conditionChoice === "Other" && (
                  <input
                    value={conditionOther}
                    onChange={(e) => {
                      setConditionOther(e.target.value);
                      setCondition(e.target.value);
                    }}
                    placeholder="Describe the condition"
                    style={{ ...inputStyle, marginTop: 8 }}
                  />
                )}
                <label style={{
                  marginTop: 10, display: "flex", alignItems: "center", gap: 8,
                  cursor: "pointer",
                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                  letterSpacing: "0.06em", color: "hsl(var(--ink))",
                }}>
                  <input
                    type="checkbox"
                    checked={deadstock}
                    onChange={(e) => setDeadstock(e.target.checked)}
                    style={{ width: 16, height: 16, accentColor: "hsl(var(--rust))", cursor: "pointer" }}
                  />
                  Deadstock <span style={{ color: "hsl(var(--ink-2))", textTransform: "none", letterSpacing: 0 }}>— unworn / unused vintage</span>
                </label>
              </div>
              <div>
                <label style={labelStyle}>Caption</label>
                <textarea value={caption} onChange={(e) => setCaption(e.target.value.slice(0, 600))}
                  placeholder="Story, sourcing notes, why you love it…" rows={4}
                  style={{ ...inputStyle, fontSize: 13, resize: "vertical", minHeight: 90 }} />
                <div style={{ marginTop: 4, fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, color: "hsl(var(--ink-2))" }}>
                  {caption.length}/600
                </div>
              </div>
            </div>
          )}

          {/* ─── WHERE ─── */}
          {step === "where" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <h2 style={sectionTitle}>Where is it?</h2>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {(["selling", "kept"] as const).map((k) => {
                  const active = whereKind === k;
                  return (
                    <button key={k} onClick={() => setWhereKind(k)} style={{
                      padding: "14px 12px", textAlign: "left",
                      background: active ? "hsl(var(--ink))" : "hsl(var(--paper))",
                      color: active ? "hsl(var(--cream))" : "hsl(var(--ink))",
                      border: "1px solid hsl(var(--ink))", cursor: "pointer",
                      display: "flex", flexDirection: "column", gap: 4,
                    }}>
                      <span className="caps" style={{ fontSize: 9, letterSpacing: "0.1em" }}>
                        {k === "selling" ? "For sale" : "Just sharing"}
                      </span>
                      <span style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 16 }}>
                        {k === "selling" ? "Listed somewhere" : "No listing"}
                      </span>
                    </button>
                  );
                })}
              </div>

              {whereKind === "selling" && (
                <>
                  <div>
                    <label style={labelStyle}>
                      Your platforms {selectedPlatforms.length > 0 && (
                        <span style={{ color: "hsl(var(--ink-2))" }}>
                          ({selectedPlatforms.length} selected)
                        </span>
                      )}
                    </label>

                    {/* Selected platforms — removable chips. */}
                    {selectedPlatforms.length > 0 ? (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {selectedPlatforms.map((name) => (
                          <span key={name} style={{
                            display: "inline-flex", alignItems: "center", gap: 6,
                            padding: "6px 6px 6px 12px",
                            background: "hsl(var(--ink))", color: "hsl(var(--cream))",
                            border: "1px solid hsl(var(--ink))", borderRadius: 999,
                            fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                            letterSpacing: "0.06em",
                          }}>
                            {name}
                            <button
                              onClick={() => togglePlatform(name)}
                              aria-label={`Remove ${name}`}
                              style={{
                                width: 20, height: 20, borderRadius: "50%",
                                border: "none", background: "rgba(250,246,236,0.15)",
                                color: "hsl(var(--cream))", cursor: "pointer",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 14, lineHeight: 1, padding: 0,
                              }}
                            >×</button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p style={{
                        margin: 0, padding: "10px 12px",
                        fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 13,
                        color: "hsl(var(--ink-2))",
                        border: "1px dashed hsl(var(--ink-2))", background: "hsl(var(--paper))",
                      }}>
                        No platforms yet. Add one below to share a listing link.
                      </p>
                    )}

                    {/* Add picker — only shows platforms not already selected. */}
                    {(() => {
                      const remaining = PLATFORMS.filter(
                        (p) => !selectedPlatforms.includes(p.name),
                      );
                      if (remaining.length === 0) return null;
                      return (
                        <div style={{ marginTop: 12 }}>
                          <div style={{
                            fontFamily: "'IBM Plex Mono', monospace", fontSize: 9,
                            letterSpacing: "0.1em", textTransform: "uppercase",
                            color: "hsl(var(--ink-2))", marginBottom: 6,
                          }}>
                            + Add platform
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                            {remaining.map((p) => (
                              <button
                                key={p.name}
                                onClick={() => togglePlatform(p.name)}
                                style={{
                                  padding: "8px 14px",
                                  background: "hsl(var(--paper))",
                                  color: "hsl(var(--ink))",
                                  border: "1px dashed hsl(var(--ink))",
                                  borderRadius: 999,
                                  cursor: "pointer",
                                  fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                                  letterSpacing: "0.06em",
                                }}
                              >+ {p.name}</button>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                  {/* Per-platform inputs — one block per selected pill. */}
                  {selectedPlatforms.map((name) => {
                    const def = platformByName(name);
                    const err = handleErrors[name];
                    if (name === "In person") {
                      return (
                        <div key={name}>
                          <label style={labelStyle}>
                            In person — address or map link <span style={{ color: "hsl(var(--ink-2))" }}>(optional)</span>
                          </label>
                          <input
                            type="text"
                            inputMode="text"
                            value={urls[name] ?? ""}
                            onChange={(e) =>
                              setUrls((p) => ({ ...p, [name]: e.target.value.slice(0, 500) }))
                            }
                            placeholder="123 Main St, Brooklyn NY  or  https://maps.app.goo.gl/…"
                            maxLength={500}
                            style={inputStyle}
                          />
                          <div style={{
                            marginTop: 6,
                            fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: 10, letterSpacing: "0.05em",
                            color: "hsl(var(--ink-2))",
                          }}>
                            Type an address or paste a Google/Apple Maps share link.
                          </div>
                        </div>
                      );
                    }
                    if (name === "Personal Site") {
                      return (
                        <div key={name}>
                          <label style={labelStyle}>
                            Personal Site — URL <span style={{ color: "hsl(var(--ink-2))" }}>(optional)</span>
                          </label>
                          <input
                            type="url"
                            inputMode="url"
                            value={urls[name] ?? ""}
                            onChange={(e) =>
                              setUrls((p) => ({ ...p, [name]: e.target.value.slice(0, 500) }))
                            }
                            placeholder="https://yourshop.com"
                            maxLength={500}
                            style={inputStyle}
                          />
                        </div>
                      );
                    }
                    if (def?.prefix) {
                      const helpId = `handle-help-${name}`;
                      return (
                        <div key={name}>
                          <label style={labelStyle}>
                            {name} username <span style={{ color: "hsl(var(--ink-2))" }}>(optional)</span>
                          </label>
                          <div style={{
                            display: "flex", alignItems: "stretch",
                            border: `1px solid ${err ? "hsl(var(--rust))" : "hsl(var(--ink))"}`,
                            background: "hsl(var(--paper))",
                          }}>
                            <span style={{
                              padding: "10px 10px", background: "hsl(var(--cream))",
                              borderRight: "1px solid hsl(var(--ink))",
                              fontFamily: "'IBM Plex Mono', monospace", fontSize: 11,
                              color: "hsl(var(--ink-2))", display: "flex", alignItems: "center",
                              maxWidth: "55%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>
                              {def.prefix}
                            </span>
                            <input
                              value={usernames[name] ?? ""}
                              onChange={(e) =>
                                setUsernames((p) => ({ ...p, [name]: e.target.value.slice(0, 80) }))
                              }
                              placeholder="yourusername"
                              aria-invalid={!!err}
                              aria-describedby={helpId}
                              maxLength={80}
                              style={{
                                flex: 1, minWidth: 0, padding: "10px 12px", border: "none",
                                background: "transparent", color: "hsl(var(--ink))",
                                fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 14, outline: "none",
                              }}
                            />
                          </div>
                          <div
                            id={helpId}
                            role={err ? "alert" : undefined}
                            style={{
                              marginTop: 6,
                              fontFamily: "'IBM Plex Mono', monospace",
                              fontSize: 10,
                              letterSpacing: "0.05em",
                              color: err ? "hsl(var(--rust))" : "hsl(var(--ink-2))",
                            }}
                          >
                            {err ?? (def.hint ? `Allowed: ${def.hint}` : "")}
                          </div>
                        </div>
                      );
                    }
                    return (
                      <div key={name}>
                        <label style={labelStyle}>
                          {name} — listing URL <span style={{ color: "hsl(var(--ink-2))" }}>(optional)</span>
                        </label>
                        <input
                          value={urls[name] ?? ""}
                          onChange={(e) =>
                            setUrls((p) => ({ ...p, [name]: e.target.value.slice(0, 500) }))
                          }
                          placeholder="Paste a listing link"
                          maxLength={500}
                          style={inputStyle}
                        />
                      </div>
                    );
                  })}
                </>
              )}

              <div>
                <label style={labelStyle}>Location <span style={{ color: "hsl(var(--ink-2))" }}>(optional)</span></label>
                <input value={city} onChange={(e) => setCity(e.target.value)}
                  placeholder="City — helps Nearby finds" style={inputStyle} />
              </div>
            </div>
          )}

          {/* ─── REVIEW ─── */}
          {step === "review" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              <h2 style={sectionTitle}>Looks good?</h2>

              {/* Preview */}
              <div style={{ border: "1px solid hsl(var(--ink))", background: "hsl(var(--paper))" }}>
                {slots[0] && (
                  <div style={{ aspectRatio: "1", overflow: "hidden", background: "hsl(var(--ink))", position: "relative" }}>
                    <img
                      src={slotPreview(slots[0])}
                      alt="cover"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    {isVerifiedFromScan && (
                      <div style={{
                        position: "absolute", margin: "10px",
                        background: "hsl(var(--rust))", color: "hsl(var(--cream))",
                        padding: "4px 8px", fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase",
                      }}>Fig Verified</div>
                    )}
                  </div>
                )}
                <div style={{ padding: 14 }}>
                  <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 16, color: "hsl(var(--ink))" }}>
                    {title || "Untitled"}
                  </div>
                  {(brand || size) && (
                    <div style={{ marginTop: 4, fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: "0.08em", color: "hsl(var(--ink-2))", textTransform: "uppercase" }}>
                      {[brand, size].filter(Boolean).join(" · ")}
                    </div>
                  )}
                  <textarea
                    value={caption}
                    onChange={(e) => setCaption(e.target.value.slice(0, 600))}
                    placeholder="Add a caption…"
                    rows={3}
                    aria-label="Caption"
                    style={{
                      marginTop: 10, width: "100%", boxSizing: "border-box",
                      padding: "8px 10px",
                      fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 13,
                      lineHeight: 1.4, color: "hsl(var(--ink))",
                      background: "hsl(var(--paper))",
                      border: "1px dashed hsl(var(--ink-2))",
                      outline: "none", resize: "vertical",
                    }}
                  />
                  <div style={{ marginTop: 12, display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {whereKind === "selling" ? (
                      selectedPlatforms.map((name) => (
                        <span key={name} className="caps" style={{
                          background: "hsl(var(--ink))", color: "hsl(var(--cream))",
                          padding: "3px 7px", fontSize: 9, letterSpacing: "0.1em",
                        }}>For sale · {name}</span>
                      ))
                    ) : (
                      <span className="caps" style={{
                        background: "hsl(var(--cream))", color: "hsl(var(--ink))",
                        padding: "3px 7px", fontSize: 9, letterSpacing: "0.1em",
                        border: "1px solid hsl(var(--ink))",
                      }}>Just sharing</span>
                    )}
                    {city && (
                      <span className="caps" style={{
                        background: "transparent", color: "hsl(var(--ink-2))",
                        padding: "3px 7px", fontSize: 9, letterSpacing: "0.1em",
                        border: "1px dashed hsl(var(--ink-2))",
                      }}>{city}</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Edit jumps */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                {(["photos", "details", "where"] as const).map((s) => (
                  <button key={s} onClick={() => setStep(s)} style={{
                    padding: "10px", background: "transparent", color: "hsl(var(--ink))",
                    border: "1px dashed hsl(var(--ink))", cursor: "pointer",
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                    letterSpacing: "0.1em", textTransform: "uppercase",
                  }}>Edit {STEP_LABEL[s]}</button>
                ))}
              </div>

              <p style={{
                fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                color: isVerifiedFromScan ? "hsl(var(--rust))" : "hsl(var(--ink-2))",
                letterSpacing: "0.05em", lineHeight: 1.5,
              }}>
                {isVerifiedFromScan
                  ? "Your piece is verified vintage! The Fig verified badge travels with this post."
                  : "This post won't carry the FIG verified badge. You can run verification later from the post."}
              </p>
            </div>
          )}
        </div>

        {/* Action bar */}
        <div style={{
          position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)",
          width: "100%", maxWidth: 430,
          background: "hsl(var(--paper))", borderTop: "1px solid hsl(var(--ink))",
          padding: "14px 20px calc(env(safe-area-inset-bottom, 8px) + 14px)",
          display: "flex", gap: 10,
        }}>
          <button onClick={goBack} disabled={posting} style={{
            padding: "14px 18px", background: "transparent", color: "hsl(var(--ink))",
            border: "1px solid hsl(var(--ink))", cursor: posting ? "wait" : "pointer",
            fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}>
            {idx === 0 ? "Cancel" : "Back"}
          </button>
          <button onClick={goNext} disabled={!canAdvance || posting} className="btn btn-rust" style={{
            flex: 1, padding: "14px", fontSize: 15,
            opacity: (!canAdvance || posting) ? 0.5 : 1,
            cursor: (!canAdvance || posting) ? "not-allowed" : "pointer",
          }}>
            {step === "review"
              ? (posting ? "Posting…" : "Post")
              : "Continue →"}
          </button>
        </div>
      </div>
    </MobileFrame>
  );
}